import React, { useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { message, Pagination, Popconfirm, Switch, Table, Tooltip } from "antd";
import DebounceInput from "../../../common/DebounceInput";
import Button from "../../../common/Button";
import useAuthContext from "../../../hooks/useAuthContext";
import {
  useAdminBatchSkus,
  useResendToSegregation,
  useSetClientClarificationPending,
} from "../../../hooks/useAdmin";
import AdminDownloadInputModal from "../AdminDownloadInputModal";
import AdminRaiseRequestModal from "../AdminRaiseRequestModal";
import {
  canDownloadBatchOutput,
  formatUserLabel,
  isBstBatch,
  queryErrorMessage,
  storedIsInternal,
} from "../helpers";
import "../AdminBatchList/style.scss";

const AdminSkuList = () => {
  const { batchId } = useParams();
  const {
    user: { isInternal },
  } = useAuthContext();
  const [pageNum, setPageNum] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [skuQuery, setSkuQuery] = useState("");
  const [downloadSkuNames, setDownloadSkuNames] = useState(undefined);
  const [raiseSku, setRaiseSku] = useState(null);

  const { data: page, isLoading, isError, error, refetch } = useAdminBatchSkus(
    batchId,
    {
      pageNum,
      pageSize,
      q: skuQuery,
    },
    { enabled: Boolean(isInternal || storedIsInternal()) }
  );
  const batch = page?.batch;
  const skus = page?.skus || [];
  const totalCount = page?.totalCount || 0;

  const {
    mutate: setClarificationPending,
    isLoading: isUpdatingClarification,
    variables: clarificationVars,
  } = useSetClientClarificationPending(
    () => refetch(),
    (err) =>
      message.error(
        err?.response?.data?.message || "Could not update client clarification."
      )
  );

  const { mutate: resend, isLoading: isResending, variables: resendVars } = useResendToSegregation(
    (res) => {
      if (res.status === 200) {
        message.success("SKU sent back to segregation.");
        refetch();
      }
    },
    (err) =>
      message.error(err?.response?.data?.message || "Could not resend this SKU.")
  );

  if (!isInternal && !storedIsInternal()) return <Navigate to="/order-list" replace />;

  return (
    <div className="admin_sku_list">
      <div className="admin_sku_list_content">
        <Link className="back_link" to="/admin/batches">
          ← Back to Batches (Admin)
        </Link>
        <div className="admin_header">
          <h1>
            {batch?.batchNumber || ""} {batch?.name || "Batch SKUs"}
          </h1>
          <div className="batch_meta">
            {batch?.source?.name && <span>{batch.source.name}</span>}
            {batch?.user && <span>{formatUserLabel(batch.user)}</span>}
            {batch?.displayStage && <span>{batch.displayStage}</span>}
            {batch?.fluidSegregation && <span className="fluid_badge">Fluid</span>}
          </div>
        </div>
        <div className="sku_toolbar">
          <div className="filter_field">
            <label>Search SKUs</label>
            <DebounceInput
              searchName={skuQuery}
              setSearchName={(value) => {
                setSkuQuery(value);
                setPageNum(1);
              }}
              placeholder="Filter SKUs in this batch"
              allowClear
            />
          </div>
          <div className="sku_toolbar_actions">
            <Button
              width="180px"
              height="40px"
              text="Download all input"
              handleClick={() => setDownloadSkuNames({ kind: "input", skuNames: null })}
            />
            {batch && !isBstBatch(batch) && (
              <Tooltip
                title={
                  canDownloadBatchOutput(batch)
                    ? undefined
                    : "Available once the batch is in review."
                }
              >
                <span>
                  <Button
                    width="180px"
                    height="40px"
                    text="Download all output"
                    isDisabled={!canDownloadBatchOutput(batch)}
                    handleClick={() => setDownloadSkuNames({ kind: "output" })}
                  />
                </span>
              </Tooltip>
            )}
          </div>
        </div>
        <div className="list_wrapper">
          {isError && (
            <p className="admin_error">
              {queryErrorMessage(error)}{" "}
              <button type="button" className="text_action" onClick={() => refetch()}>
                Retry
              </button>
            </p>
          )}
          <Table
            pagination={false}
            loading={isLoading}
            scroll={{ y: "100%" }}
            rowKey="_id"
            dataSource={skus}
            locale={{ emptyText: isError ? " " : "No SKUs in this batch." }}
            columns={[
              {
                title: "SKU",
                render: (_, row) => row.displayName || row.draftSkuName,
              },
              { title: "Stage", dataIndex: "displayStage", align: "center" },
              { title: "Status", dataIndex: "displayStatus", align: "center" },
              { title: "Files", dataIndex: "fileCount", align: "center" },
              {
                title: "Client Clarification",
                align: "center",
                render: (_, row) => {
                  const clarificationBlocked =
                    row.stage === "review" && !row.clientClarificationPending;
                  const clarificationLoading =
                    isUpdatingClarification &&
                    clarificationVars?.draftSkuId === row._id;

                  return (
                    <Tooltip
                      title={
                        clarificationBlocked
                          ? "Cannot enable while the SKU is in review."
                          : undefined
                      }
                    >
                      <Switch
                        size="small"
                        checked={Boolean(row.clientClarificationPending)}
                        disabled={clarificationBlocked || clarificationLoading}
                        loading={clarificationLoading}
                        onChange={(checked) =>
                          setClarificationPending({
                            draftSkuId: row._id,
                            clientClarificationPending: checked,
                          })
                        }
                      />
                    </Tooltip>
                  );
                },
              },
              {
                title: "Actions",
                align: "center",
                render: (_, row) => (
                  <div className="admin_actions">
                    <button
                      type="button"
                      className="text_action"
                      onClick={() =>
                        setDownloadSkuNames({ kind: "input", skuNames: [row.draftSkuName] })
                      }
                    >
                      Download input
                    </button>
                    <button
                      type="button"
                      className="text_action"
                      onClick={() => setRaiseSku(row)}
                    >
                      Raise request
                    </button>
                    {row.canResendToSegregation ? (
                      <Popconfirm
                        title="Resend this SKU to segregation?"
                        description="This deactivates the production SKU and returns it to unclaimed segregation."
                        okText="Resend"
                        onConfirm={() => resend({ skuId: row.skuId })}
                      >
                        <button
                          type="button"
                          className="text_action"
                          disabled={isResending && resendVars?.skuId === row.skuId}
                        >
                          Resend to segregation
                        </button>
                      </Popconfirm>
                    ) : (
                      <Tooltip title="Only fluid batches with an active production SKU can be resent.">
                        <button type="button" className="text_action" disabled>
                          Resend to segregation
                        </button>
                      </Tooltip>
                    )}
                  </div>
                ),
              },
            ]}
          />
        </div>
        <div className="pagination_wrapper">
          <Pagination
            total={totalCount}
            current={pageNum}
            pageSize={pageSize}
            onChange={(page, size) => {
              setPageNum(page);
              setPageSize(size);
            }}
          />
        </div>
      </div>
      {downloadSkuNames !== undefined && (
        <AdminDownloadInputModal
          batchId={batchId}
          kind={downloadSkuNames?.kind}
          skuNames={downloadSkuNames?.skuNames}
          onClose={() => setDownloadSkuNames(undefined)}
        />
      )}
      {raiseSku && (
        <AdminRaiseRequestModal
          sku={raiseSku}
          batchId={batchId}
          batchNumber={batch?.batchNumber}
          onClose={() => setRaiseSku(null)}
        />
      )}
    </div>
  );
};

export default AdminSkuList;
